import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { AuthCard, Spinner } from '@sentinel/ui';
import { isSafeReturnTo, useSession } from '@sentinel/auth-core';
import { useLaunchProducts } from '../features/products/use-products';
import { resolveProductDestination } from '../features/products/resolve-launch-target';

const search = z.object({ return_to: z.string().optional() });

export function LaunchResolver() {
  const { t } = useTranslation('products');
  const { user, loading } = useSession();
  const { return_to } = useSearch({ from: '/launch' });
  const navigate = useNavigate();
  // Only fetch products once the session is established AND there is no safe
  // return_to to honor first (the callback takes precedence over product logic).
  const enabled = !loading && !!user && !isSafeReturnTo(return_to);
  const products = useLaunchProducts(enabled);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      void navigate({ to: '/login', replace: true });
      return;
    }
    // Callback precedence: an explicit, safe return_to wins over product routing.
    if (isSafeReturnTo(return_to)) {
      window.location.assign(return_to);
      return;
    }
    if (products.isLoading) return;
    const dest = resolveProductDestination({
      isError: products.isError,
      total: products.total,
      items: products.items,
    });
    if (dest.kind === 'external') {
      window.location.assign(dest.url);
      return;
    }
    if (dest.kind === 'chooser') {
      void navigate({ to: '/select-product', replace: true });
      return;
    }
    void navigate({ to: '/account', replace: true });
  }, [
    loading,
    user,
    return_to,
    products.isLoading,
    products.isError,
    products.total,
    products.items,
    navigate,
  ]);

  return (
    <AuthCard testId="launch-resolving">
      <Spinner label={t('resolving')} />
    </AuthCard>
  );
}

export const Route = createFileRoute('/launch')({
  validateSearch: search,
  component: LaunchResolver,
});
